import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeader from "@/components/base/SectionHeader";

const SUBMIT_URL = "https://readdy.ai/api/form/daglphptto0ceqqnhv1g";

type Status = "idle" | "loading" | "success" | "error";

export default function SubmitSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("company_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      payload.append(key, String(value).trim());
    });

    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const isSpam = /spam/i.test(responseText) || /spam/i.test(parsed?.meta?.message ?? "") || /spam/i.test(parsed?.message ?? "");
      const ok = res.ok && parsed?.code === "OK" && !isSpam;
      if (ok) {
        setStatus("success");
        form.reset();
      } else {
        setFormError(
          parsed?.meta?.message ||
            parsed?.meta?.detail ||
            parsed?.message ||
            "提交失败，请稍后再试。"
        );
        setStatus("error");
      }
    } catch {
      setFormError("网络异常，未能提交，请稍后再试。");
      setStatus("error");
    }
  }

  return (
    <section id="submit">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.35fr]">
          <div>
            <SectionHeader
              eyebrow="投稿与联系"
              eyebrowIcon="ri-mail-send-line"
              title="投稿线索 · 联系我们"
              desc="无论你有一条值得围观的大瓜线索、想参与话题共建，还是有投诉或合作想法，都可以通过右侧表单告诉我们。我们会在 24 小时内处理。"
            />
            <div className="mt-6 flex flex-col gap-4 text-sm text-foreground-700">
              <div className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3 shadow-soft">
                <i className="ri-mail-line text-primary-600" />
                <span>投稿邮箱：melons@example.com</span>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3 shadow-soft">
                <i className="ri-time-line text-primary-600" />
                <span>处理时效：工作日 24 小时内回复</span>
              </div>
              <p className="leading-relaxed text-foreground-600">
                温馨提示：提供线索时请尽量附上可核实的信息源；涉及隐私的信息我们不会公开。为方便小编评估，正文请控制在 500 字以内。
              </p>
            </div>
          </div>

          <Reveal delay={100}>
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5 shadow-soft md:p-6">
              {status === "success" ? (
                <div className="flex flex-col items-center py-12 text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
                    <i className="ri-check-double-line" />
                  </span>
                  <h3 className="mt-4 font-heading text-2xl text-foreground-900">已收到你的留言</h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-700">
                    感谢参与。我们会尽快处理你的投稿 / 反馈，如需进一步沟通会通过你留下的邮箱联系你。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-add-line" /> 再写一条
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form="本月大瓜线索投稿表单"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="nickname" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        你的昵称
                      </label>
                      <input
                        id="nickname"
                        name="nickname"
                        type="text"
                        placeholder="怎么称呼你"
                        maxLength={20}
                        className="w-full rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        联系方式（邮箱）<span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="方便我们回信联系你"
                        className="w-full rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                      留言类型 <span className="text-accent-600">*</span>
                    </label>
                    <select
                      id="topic"
                      name="topic"
                      required
                      defaultValue=""
                      className="w-full rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                    >
                      <option value="" disabled>
                        请选择留言类型
                      </option>
                      <option value="吃瓜线索投稿">吃瓜线索投稿</option>
                      <option value="投诉与举报">投诉与举报</option>
                      <option value="合作与建议">合作与建议</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="mb-1.5 flex items-center justify-between text-sm font-medium text-foreground-800">
                      <span>
                        留言内容 <span className="text-accent-600">*</span>
                      </span>
                      <span className="text-xs font-normal text-foreground-500">最多 500 字</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="请描述你的线索或反馈，尽量附上可核实的信息源……"
                      className="w-full resize-none rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
                    />
                  </div>

                  <div className="field-trap" aria-hidden="true">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" readOnly />
                  </div>

                  {status === "error" && (
                    <p className="flex items-start gap-2 rounded-lg border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-sm text-accent-700">
                      <i className="ri-error-warning-line mt-0.5" />
                      <span>{formError}</span>
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === "loading" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" /> 提交中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line" /> 提交留言
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}